import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { ResourceService } from './resource-service';
import { ProductAds, ProductAdState, ManagedScope } from '../models/product-ads';
import { SortDescriptor } from '@progress/kendo-data-query';
import { Observable } from 'rxjs';
import { format } from 'date-fns';
import { stringifyJSONWithDates } from '@stockaid/utils';
import { CampaignProductAd } from '../models/campaign-product-ad';

const CAMPAIGN_PRODUCT_AD_URL = `/api/amz-advertising/product-ads`;

interface IFilterItemsByCampaign {
  skip?: number;
  take?: number;
  filter?: any;
  sort?: SortDescriptor[];
  dateRange?: { startDate: Date; endDate: Date };
}

interface UpdateStateByItemKeysPayload {
  state: ProductAdState,
  keys: string[],
}

interface UpdateStateByAdIdsPayload {
  state: ProductAdState,
  adIds: string[],
}

interface BulkUpdateProductAdByQueryPayload {
  state: ProductAdState,
  filter: any,
  excludeIds?: string[],
  managedScope?: ManagedScope,
}

interface AutoModeItem {
  key: string;
  level: 0 | 1;
  campaignId?: string;
  adId?: string;
}

interface ExcludeItem {
  key: string;
  level: 0 | 1;
  campaignId?: string;
  adId?: string;
}

type UpdateAutoModePayload =
  | { isAutomatic: boolean; items: AutoModeItem[] }
  | { isAutomatic: boolean; selectAll: { filter: any; excludes?: ExcludeItem[] } };

export type PreviewSelectionMode = 'explicit' | 'query';
export type PreviewChangeFilter = 'WILL_UPDATE' | 'NO_CHANGE' | 'ALL';

// Used by confirm() → /bulk-async (old format with mixed-level ExplicitItem)

export interface ItemAdsPair {
  itemKey: string;
  adIds?: string[];
}

export interface BulkExecutePayload {
  mode: PreviewSelectionMode;
  action: 'active' | 'isAutomatic' | 'managedBySystem';
  targetValue: boolean;
  managedScope?: ManagedScope;
  where?: any;
  includes?: ItemAdsPair[];
  excludes?: ItemAdsPair[];
  startDate?: string;
  endDate?: string;
}

// Used by loadPreview() and loadBulkCounts() → /preview-bulk and /preview-bulk/count (new format with level-separated arrays)
interface PreviewBulkBasePayload {
  mode: PreviewSelectionMode;
  action: 'active' | 'isAutomatic' | 'managedBySystem';
  targetValue: boolean;
  managedScope?: ManagedScope;
  where?: any;
  includes?: ItemAdsPair[];
  excludes?: ItemAdsPair[];
  startDate?: string;
  endDate?: string;
}

export interface PreviewBulkPayload extends PreviewBulkBasePayload {
  offset?: number;
  limit?: number;
  changeFilter?: PreviewChangeFilter;
  searchTerm?: string;
}

type PreviewBulkCountPayload = PreviewBulkBasePayload;

export interface BulkCountResponse {
  skuWillUpdate: number;
  skuAlreadyCurrent: number;
  adWillUpdate: number;
  adAlreadyCurrent: number;
}

interface PreviewBulkItem {
  itemKey: string;
  sku: string;
  currentValue: boolean;
  campaigns: Array<{
    adId: string;
    campaignName: string;
    currentValue: boolean;
  }>;
}

export interface PreviewBulkResponse {
  items: PreviewBulkItem[];
  total: number;
}

@Injectable({
  providedIn: 'root',
})
export class ProductAdsService extends ResourceService<ProductAds> {
  constructor(protected httpClient: HttpClient) {
    super(httpClient, CAMPAIGN_PRODUCT_AD_URL);
  }

  groupBySKU(filter: IFilterItemsByCampaign): Observable<ProductAds[]> {
    const params = this.buildAggregatedParams(filter);
    return this.httpClient.get<ProductAds[]>(`${this.apiUrl}/aggregated`, { params });
  }

  countGroupBySKU(filter: IFilterItemsByCampaign): Observable<number> {
    const params = this.buildAggregatedParams(filter);
    return this.httpClient.get<number>(`${this.apiUrl}/aggregated/count`, { params });
  }

  updateStateByItemKeys(payload: UpdateStateByItemKeysPayload) {
    return this.httpClient.post(`${this.apiUrl}/product-ad-key/state`, payload);
  }

  updateStateByAdIds(payload: UpdateStateByAdIdsPayload) {
    return this.httpClient.post(`${this.apiUrl}/state`, payload);
  }

  bulkUpdateProductAdByQuery(payload: BulkUpdateProductAdByQueryPayload) {
    return this.httpClient.post(`${this.apiUrl}/bulk`, payload);
  }

  previewBulk(payload: PreviewBulkPayload): Observable<PreviewBulkResponse> {
    return this.httpClient.post<PreviewBulkResponse>(`${this.apiUrl}/preview-bulk`, payload);
  }

  previewBulkCount(payload: PreviewBulkCountPayload): Observable<BulkCountResponse> {
    return this.httpClient.post<BulkCountResponse>(`${this.apiUrl}/preview-bulk/count`, payload);
  }

  enqueueBulkStateByQuery(payload: BulkExecutePayload): Observable<{ status: string }> {
    return this.httpClient.post<{ status: string }>(`${this.apiUrl}/bulk-async`, payload);
  }

  updateAutoMode(payload: UpdateAutoModePayload) {
    return this.httpClient.post(`${this.apiUrl}/auto-mode`, payload);
  }

  updateManaged(payload: { adId: string; managed: boolean }) {
    return this.httpClient.post<{ success: boolean; adId: string; managed: boolean }>(
      `${this.apiUrl}/managed`,
      payload,
    );
  }

  updateManagedByItemKey(payload: { itemKey: string; managed: boolean }) {
    return this.httpClient.post<{ success: boolean; itemKey: string; managed: boolean }>(
      `${this.apiUrl}/managed-by-item-key`,
      payload,
    );
  }

  updateStockoutSettings(itemKey: string, minDays: number | null, maxDays: number | null) {
    return this.httpClient.post<{
      success: boolean;
      itemKey: string;
      inventoryStatus: string;
      suggestAction: string;
      daysUntilStockOut: number;
      minDaysStockout: number;
      maxDaysStockout: number;
    }>(`${this.apiUrl}/${itemKey}/stockout-settings`, { minDays, maxDays });
  }
  
  getActionCenterStats(): Observable<any> {
    return this.httpClient.get<any>(`${this.apiUrl}/action-center/stats`);
  }

  getCampaignsByItemKey(itemKey: string, filter: IFilterItemsByCampaign) {
    const params = this.buildAggregatedParams(filter);

    return this.httpClient.get<CampaignProductAd[]>(`${this.apiUrl}/${itemKey}/campaigns`, { params });
  }

  private buildAggregatedParams(filter: IFilterItemsByCampaign): HttpParams {
    const sortArray = (filter.sort ?? []).map((s) => ({ field: s.field, direction: s.dir ?? 'asc' }));
    let params = new HttpParams()
      .set('offset', String(filter.skip ?? 0))
      .set('limit', String(filter.take ?? 20))
      .set('sort', JSON.stringify(sortArray))
      .set('where', stringifyJSONWithDates(filter.filter));
    if (filter.dateRange) {
      params = params
        .set('startDate', format(filter.dateRange.startDate, 'yyyy-MM-dd'))
        .set('endDate', format(filter.dateRange.endDate, 'yyyy-MM-dd'));
    }
    return params;
  }
}
